import React, { useEffect, useState, useRef, useCallback } from "react";
import { getToken, loadCss, loadScript, resolveComponentLocale } from "helpers/function";
import appConfig from "config/appConfig";
import axios from "axios";
import { connect } from "react-redux";
import { ToastBottomEnd } from "views/components/Toast";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import ls from "localstorage-slim";

import {
  toastConnectErrorMessageConfig,
  toastConnectSuccessMessageConfig,
  toastErrorMessageWithParameterConfig,
} from "config/toastConfig";
import Loader from "views/components/Loader";
import "./LoginCss.css";
import ForgotForm from "./ForgotForm";
import ReinitialisationForm from "./ReinitialisationForm";
import ConnexionForm from "./ConnexionForm";
import { PasswordConfirmation } from "views/components/ConfirmationAlert";
import { passwordExpireConfig } from "config/confirmConfig";
import { useTranslation } from "react-i18next";
import ForgotPasswordForm from "./ForgotPasswordForm";
import Verify2FA from "./Verify2FA";

loadCss("/assets/css/pages/login/login-1.css");
loadScript("/assets/js/pages/custom/login/login-1.js");

const LoginPage = (props) => {
  const { t, ready, i18n } = useTranslation();

  // Refs pour le nettoyage
  const intervalRef = useRef(null);
  const abortControllerRef = useRef(null);

  ls.config.encrypt = true;

  const tokenData = getToken(window.location.href);
  const defaultError = {
    username: "",
    password: "",
  };
  const defaultData = {
    username: "",
    password: "",
  };

  const [load, setLoad] = useState(true);
  const [data, setData] = useState(defaultData);
  const [componentData, setComponentData] = useState(undefined);
  const [error, setError] = useState(defaultError);
  const [startRequest, setStartRequest] = useState(false);
  const [expiresIn, setExpireIn] = useState(null);

  useEffect(() => {
    let mounted = true;
    abortControllerRef.current = new AbortController();

    async function fetchData() {
      try {
        const response = await axios.get(
          appConfig.apiDomaine + "/components/retrieve-by-name/connection",
          { signal: abortControllerRef.current.signal }
        );

        if (mounted) {
          setComponentData(resolveComponentLocale(response.data));
          localStorage.removeItem("Dtimeout");
        }
      } catch (error) {
        if (error.name !== "AbortError" && mounted) {
          localStorage.removeItem("Dtimeout");
        }
      } finally {
        if (mounted) {
          setLoad(false);
        }
      }
    }

    fetchData();

    return () => {
      mounted = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const startDecounter = useCallback((minute, second = 0) => {
    // Nettoyer l'interval existant
    clearCountdownInterval();

    setExpireIn({
      minute: minute,
      second: second,
    });

    ls.set(
      "decount",
      JSON.stringify({
        minute: minute,
        second: second,
      })
    );

    intervalRef.current = setInterval(() => {
      const decount = JSON.parse(ls.get("decount"));
      if (decount) {
        if (decount.second === 0 && decount.minute === 0) {
          setExpireIn(null);
          ls.remove("decount");
          clearCountdownInterval();
        } else {
          if (decount.second === 0) {
            decount.minute = decount.minute - 1;
            decount.second = 59;
          } else {
            decount.second = decount.second - 1;
          }
          setExpireIn(decount);
          ls.set("decount", JSON.stringify(decount));
        }
      } else {
        ls.remove("decount");
        setExpireIn(null);
        clearCountdownInterval();
      }
    }, 1000);
  }, []);

  useEffect(() => {
    const decount = JSON.parse(ls.get("decount"));
    if (decount) {
      startDecounter(decount.minute, decount.second);
    }

    // Nettoyage à la déconnexion du composant
    return () => {
      clearCountdownInterval();
    };
  }, [startDecounter]);

  const clearCountdownInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    // Nettoyer aussi window.timeIntervale si elle existe
    if (window.timeIntervale) {
      clearInterval(window.timeIntervale);
      window.timeIntervale = null;
    }
  };

  const onChangeUserName = (e) => {
    const newData = { ...data };
    newData.username = e.target.value;
    setData(newData);
  };

  const onChangePassword = (e) => {
    const newData = { ...data };
    newData.password = e.target.value;
    setData(newData);
  };

  const onViewPassword = (e) => {
    let input = document.getElementById("password");
    let icon = document.getElementById("icon");
    if (input && icon) {
      if (input.type === "password") {
        input.type = "text";
        icon.className = "fa fa-eye";
      } else {
        input.type = "password";
        icon.className = "fa fa-eye-slash";
      }
    }
  };

  const onClickConnectButton = async (e) => {
    e.preventDefault();
    setStartRequest(true);

    const formData = {
      grant_type: appConfig.listConnectData[props.plan].grant_type,
      client_id: appConfig.listConnectData[props.plan].client_id,
      client_secret: appConfig.listConnectData[props.plan].client_secret,
      username: data.username,
      password: data.password,
    };

    try {
      const loginResponse = await axios.post(
        appConfig.apiDomaine + `/login`,
        formData
      );

      const token = loginResponse.data.access_token;
      const refresh_token = loginResponse.data.refresh_token;
      const expire_in = loginResponse.data.expires_in;

      try {
        const userResponse = await axios.get(appConfig.apiDomaine + `/login`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        ToastBottomEnd.fire(toastConnectSuccessMessageConfig());
        ls.set("userData", JSON.stringify(userResponse.data));
        ls.set("token", token);
        ls.set("expire_in", expire_in);

        var date = new Date();
        date.setSeconds(date.getSeconds() + expire_in);
        ls.set("date_expire", date);
        ls.set("refresh_token", refresh_token);

        // A new session always starts in French, regardless of whatever
        // language was active last time this browser was used.
        ls.set("i18nextLng", "fr");
        i18n.changeLanguage("fr");

        // Nettoyer les intervals avant la redirection
        clearCountdownInterval();

        setTimeout(() => {
          window.location.href = "/dashboard";
        }, 500);
      } catch (userError) {
        setStartRequest(false);
        ToastBottomEnd.fire(toastConnectErrorMessageConfig());
      }
    } catch (error) {
      console.log(error);
      setStartRequest(false);
      setError(defaultError);

      if (error.response?.data?.status === 403) {
        const message = error.response.data.message;

        if (error.response.data.expire_in !== undefined) {
          ls.remove("decount");
          clearCountdownInterval();
          startDecounter(
            Math.trunc(error.response.data.expire_in / 60),
            error.response.data.expire_in % 60
          );
        } else {
          setExpireIn(null);
          setError({ password: message });
        }

        ToastBottomEnd.fire(
          toastErrorMessageWithParameterConfig(message)
        );
      } else if (error.response?.data?.status === 400) {
        setExpireIn(null);
        ToastBottomEnd.fire(
          toastErrorMessageWithParameterConfig(error.response.data.message)
        );
      } else if (
        error.response?.status === 401 ||
        error.response?.status === 422
      ) {
        setExpireIn(null);
        let errorable = error?.response?.data?.message;

        if (
          errorable &&
          !errorable.includes("The provided authorization grant")
        ) {
          setError({
            username: ready ? t(errorable) : "",
            password: ready ? t(errorable) : "",
          });
          ToastBottomEnd.fire(
            toastErrorMessageWithParameterConfig(t(errorable))
          );
        } else {
          setError({
            username: ready ? t("Email ou mot de passe incorrect") : "",
            password: ready ? t("Email ou mot de passe incorrect") : "",
          });
          ToastBottomEnd.fire(
            toastErrorMessageWithParameterConfig(
              t("Email ou mot de passe incorrect")
            )
          );
        }
      } else if (error.response?.data?.status === 423) {
        setExpireIn(null);
        PasswordConfirmation.fire(
          passwordExpireConfig(error.response.data.message)
        ).then((response) => {
          window.location.pathname = `/reset-password`;
        });
      } else if (error.response?.data?.status === 406) {
        setExpireIn(null);
        ToastBottomEnd.fire(
          toastErrorMessageWithParameterConfig(
            t("Désolé, vous etes déjà connecté sur un autre appareil")
          )
        );
      } else {
        setExpireIn(null);
        if (data.username === "" || data.password === "") {
          setError({
            username: ready ? t("Email ou mot de passe incorrect") : "",
            password: ready ? t("Email ou mot de passe incorrect") : "",
          });
        }
        ToastBottomEnd.fire(toastConnectErrorMessageConfig());
      }
    }
  };

  return load ? (
    <Loader />
  ) : (
    <BrowserRouter>
      <div
        className="kt-grid kt-grid--ver kt-grid--root kt-page"
        style={{ height: "100vh" }}
      >
        <div
          className="kt-grid kt-grid--hor kt-grid--root  kt-login kt-login--v1"
          id="kt_login"
        >
          <div className="kt-grid__item kt-grid__item--fluid kt-grid kt-grid--desktop kt-grid--ver-desktop kt-grid--hor-tablet-and-mobile">
            <div className="kt-grid kt-grid--ver kt-grid--root kt-page">
              <div
                className="kt-grid kt-grid--hor kt-grid--root  kt-login kt-login--v1"
                id="kt_login"
              >
                <div className="kt-grid__item kt-grid__item--fluid kt-grid kt-grid--desktop kt-grid--ver-desktop kt-grid--hor-tablet-and-mobile">
                  <div
                    className="kt-grid__item kt-grid__item--order-tablet-and-mobile-2 kt-grid kt-grid--hor kt-login__aside"
                    style={{
                      backgroundImage: `url(${
                        componentData &&
                        componentData.params.fr.background.value
                          ? appConfig.apiDomaine +
                            componentData.params.fr.background.value.url
                          : "/assets/images/satis_default_image.png"
                      })`,
                      height: "100vh",
                    }}
                  >
                    <div className="kt-grid__item">
                      <span className="kt-login__logo">
                        <img
                          alt="Logo"
                          src={
                            componentData && componentData.params.fr.logo.value
                              ? appConfig.apiDomaine +
                                componentData.params.fr.logo.value.url
                              : "/assets/images/satisLogo.png"
                          }
                          width={'200vh'}
                        />
                        <span
                          style={{
                            color: "white",
                            fontSize: "1em",
                            paddingLeft: "5px",
                          }}
                        >
                          {componentData
                            ? componentData.params.fr.version.value
                            : props.plan + appConfig.version}
                        </span>
                      </span>
                    </div>
                    <div className="kt-grid__item kt-grid__item--fluid kt-grid kt-grid--ver">
                      <div className="kt-grid__item kt-grid__item--middle">
                        <h3 className="kt-login__title">
                          {componentData
                            ? componentData.params.fr.header.value +
                              componentData.params.fr.version.value
                            : null}
                        </h3>
                        <h4 className="kt-login__subtitle">
                          {componentData
                            ? componentData.params.fr.description.value + " "
                            : null}
                        </h4>
                      </div>
                    </div>
                    <div className="kt-grid__item">
                      <div className="kt-login__info">
                        <div className="kt-login__copyright">
                          © {appConfig.appFullName(props.plan, props.year)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="kt-grid__item kt-grid__item--fluid kt-grid__item--order-tablet-and-mobile-1  kt-login__wrapper">
                    <div className="kt-login__body">
                      <Switch>
                        <Route exact path="/">
                          <ConnexionForm
                            alert={ls.get("successInvitation")}
                            componentData={componentData}
                            data={data}
                            error={error}
                            startRequest={startRequest}
                            onChangeUserName={onChangeUserName}
                            onViewPassword={onViewPassword}
                            onChangePassword={onChangePassword}
                            onClickConnectButton={onClickConnectButton}
                            expires_in={expiresIn}
                          />
                        </Route>
                        <Route exact path="/login">
                          <ConnexionForm
                            alert={ls.get("successInvitation")}
                            componentData={componentData}
                            data={data}
                            error={error}
                            startRequest={startRequest}
                            onChangeUserName={onChangeUserName}
                            onViewPassword={onViewPassword}
                            onChangePassword={onChangePassword}
                            onClickConnectButton={onClickConnectButton}
                            expires_in={expiresIn}
                          />
                        </Route>
                        <Route exact path="/login/forgot">
                          <ForgotForm />
                        </Route>

                        <Route exact path={`/forgot-password`}>
                          <ReinitialisationForm />
                        </Route>

                        <Route exact path={`/forgot-password/${tokenData}`}>
                          <ForgotPasswordForm token={tokenData} />
                        </Route>
                        <Route exact path={`/verify-2fa`}>
                          <Verify2FA />
                        </Route>
                      </Switch>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BrowserRouter>
  );
};

const mapStateToProps = (state) => {
  return {
    plan: state.plan.plan,
    year: state.year.year,
  };
};

export default connect(mapStateToProps)(LoginPage);
